/**
 * useSEO — lightweight dynamic SEO for a CRA/SPA.
 *
 * Updates canonical, OG, Twitter tags, and injects JSON-LD scripts
 * on mount; restores originals on unmount. No external dependencies.
 */
import { useEffect } from "react";

const BASE_URL = "https://erpik.com";
const DEFAULT_IMAGE = `${BASE_URL}/images/og-card.jpg`;

function setMeta(selector, attr, value) {
  if (!value) return;
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    // Parse selector like [property="og:title"] or [name="twitter:title"]
    const m = selector.match(/\[(\w+)="([^"]+)"\]/);
    if (m) el.setAttribute(m[1], m[2]);
    document.head.appendChild(el);
  }
  const prev = el.getAttribute(attr);
  el.setAttribute(attr, value);
  return prev; // so caller can restore
}

function setCanonical(url) {
  const el = document.querySelector('link[rel="canonical"]');
  if (!el) return null;
  const prev = el.getAttribute("href");
  el.setAttribute("href", url);
  return prev;
}

function injectJsonLd(id, data) {
  removeJsonLd(id);
  const script = document.createElement("script");
  script.type = "application/ld+json";
  script.id = `ld-${id}`;
  script.textContent = JSON.stringify(data, null, 2);
  document.head.appendChild(script);
}

function removeJsonLd(id) {
  document.getElementById(`ld-${id}`)?.remove();
}

export function useSEO({ title, description, canonical, ogImage, ogImageAlt, ogType, jsonLd, jsonLdId, breadcrumbs }) {
  useEffect(() => {
    const url = canonical ? `${BASE_URL}${canonical}` : BASE_URL;
    const image = ogImage ? `${BASE_URL}${ogImage}` : DEFAULT_IMAGE;
    const defaultTitle = "Sam Erpik — Technical Founder · Product Leader · Builder";
    const defaultDesc = "erpik.com is the personal site of Sam Erpik — technical founder and product leader. The ventures I've built, hands-on case studies, live computer-vision experiments, and how to work with me.";

    const resolvedTitle = title || defaultTitle;
    const resolvedDesc = description || defaultDesc;
    const resolvedOgType = ogType || "website";

    // ── Title ──────────────────────────────────────────────────────────────
    const prevTitle = document.title;
    document.title = resolvedTitle;

    // ── Canonical ──────────────────────────────────────────────────────────
    const prevCanonical = setCanonical(url);

    // ── Primary description meta (search-result snippet) ──────────────────
    const prevDesc = setMeta('[name="description"]', "content", resolvedDesc);

    // ── OG ────────────────────────────────────────────────────────────────
    const prevOgTitle  = setMeta('[property="og:title"]', "content", resolvedTitle);
    const prevOgDesc   = setMeta('[property="og:description"]', "content", resolvedDesc);
    const prevOgUrl    = setMeta('[property="og:url"]', "content", url);
    const prevOgType   = setMeta('[property="og:type"]', "content", resolvedOgType);
    const prevOgImg    = setMeta('[property="og:image"]', "content", image);
    const prevOgImgW   = setMeta('[property="og:image:width"]',  "content", "1200");
    const prevOgImgH   = setMeta('[property="og:image:height"]', "content", "630");
    const prevOgImgAlt = ogImageAlt
      ? setMeta('[property="og:image:alt"]', "content", ogImageAlt)
      : null;

    // ── Twitter ────────────────────────────────────────────────────────────
    const prevTwCard  = setMeta('[name="twitter:card"]',        "content", "summary_large_image");
    const prevTwTitle = setMeta('[name="twitter:title"]',       "content", resolvedTitle);
    const prevTwDesc  = setMeta('[name="twitter:description"]', "content", resolvedDesc);
    const prevTwImg   = setMeta('[name="twitter:image"]',       "content", image);

    // ── JSON-LD ────────────────────────────────────────────────────────────
    if (jsonLd && jsonLdId) injectJsonLd(jsonLdId, jsonLd);

    // ── BreadcrumbList JSON-LD ────────────────────────────────────────────
    // Generated as a separate schema.org graph so rich results show the
    // hierarchy and AI crawlers see explicit parent-child relationships.
    const breadcrumbId = jsonLdId ? `${jsonLdId}-breadcrumbs` : null;
    if (breadcrumbs?.length && breadcrumbId) {
      injectJsonLd(breadcrumbId, {
        "@context": "https://schema.org",
        "@type":    "BreadcrumbList",
        itemListElement: breadcrumbs.map((c, i) => ({
          "@type":   "ListItem",
          position:  i + 1,
          name:      c.name,
          item:      c.url,
        })),
      });
    }

    return () => {
      document.title = prevTitle;
      setCanonical(prevCanonical || BASE_URL);

      const d = document.querySelector('[name="description"]');
      if (d && prevDesc) d.setAttribute("content", prevDesc);

      const og = document.querySelector('[property="og:title"]');
      if (og && prevOgTitle) og.setAttribute("content", prevOgTitle);
      const ogD = document.querySelector('[property="og:description"]');
      if (ogD && prevOgDesc) ogD.setAttribute("content", prevOgDesc);
      const ogU = document.querySelector('[property="og:url"]');
      if (ogU && prevOgUrl) ogU.setAttribute("content", prevOgUrl);
      const ogT = document.querySelector('[property="og:type"]');
      if (ogT && prevOgType) ogT.setAttribute("content", prevOgType);
      const ogI = document.querySelector('[property="og:image"]');
      if (ogI && prevOgImg) ogI.setAttribute("content", prevOgImg);
      const ogIw = document.querySelector('[property="og:image:width"]');
      if (ogIw && prevOgImgW) ogIw.setAttribute("content", prevOgImgW);
      const ogIh = document.querySelector('[property="og:image:height"]');
      if (ogIh && prevOgImgH) ogIh.setAttribute("content", prevOgImgH);
      const ogIa = document.querySelector('[property="og:image:alt"]');
      if (ogIa && prevOgImgAlt) ogIa.setAttribute("content", prevOgImgAlt);

      const twC = document.querySelector('[name="twitter:card"]');
      if (twC && prevTwCard) twC.setAttribute("content", prevTwCard);
      const tw = document.querySelector('[name="twitter:title"]');
      if (tw && prevTwTitle) tw.setAttribute("content", prevTwTitle);
      const twD = document.querySelector('[name="twitter:description"]');
      if (twD && prevTwDesc) twD.setAttribute("content", prevTwDesc);
      const twI = document.querySelector('[name="twitter:image"]');
      if (twI && prevTwImg) twI.setAttribute("content", prevTwImg);

      if (jsonLdId)    removeJsonLd(jsonLdId);
      if (breadcrumbId) removeJsonLd(breadcrumbId);
    };
  }, [canonical, title, description, ogImage, ogImageAlt, ogType, jsonLd, jsonLdId, breadcrumbs]);
}
